import { useCallback, useEffect, useState } from "react";
import { Navigate, useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, LogOut, Newspaper, Users, Pencil, Trash2, Plus, X } from "lucide-react";
import { api, formatApiError } from "../../lib/api";
import { useAuth } from "../../lib/AuthContext";
import { Logo } from "../../components/Logo";

const EMPTY = { title: "", excerpt: "", content: "", category: "AI Automation", cover: "cyan", published: true };
const COVERS = ["cyan", "blue", "teal", "amber", "violet"];

export default function AdminDashboard() {
    const { user, setUser } = useAuth();
    const navigate = useNavigate();
    const [tab, setTab] = useState("blogs");
    const [blogs, setBlogs] = useState([]);
    const [leads, setLeads] = useState([]);
    const [editing, setEditing] = useState(null); // null = closed, {} = form
    const [saving, setSaving] = useState(false);

    const loadBlogs = useCallback(() => api.get("/admin/blogs").then((r) => setBlogs(r.data)).catch(() => {}), []);
    const loadLeads = useCallback(() => api.get("/admin/leads").then((r) => setLeads(r.data)).catch(() => {}), []);

    useEffect(() => {
        if (user) {
            loadBlogs();
            loadLeads();
        }
    }, [user, loadBlogs, loadLeads]);

    if (user === undefined)
        return (
            <div className="flex min-h-screen items-center justify-center bg-[#080C14] text-slate-500" data-testid="admin-loading">
                <Loader2 className="h-6 w-6 animate-spin" />
            </div>
        );
    if (user === null) return <Navigate to="/admin/login" replace />;

    const logout = async () => {
        await api.post("/auth/logout").catch(() => {});
        setUser(null);
        navigate("/admin/login");
    };

    const saveBlog = async (e) => {
        e.preventDefault();
        setSaving(true);
        try {
            if (editing.id) await api.put(`/admin/blogs/${editing.id}`, editing);
            else await api.post("/admin/blogs", editing);
            toast.success(editing.id ? "Post updated" : "Post published");
            setEditing(null);
            loadBlogs();
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setSaving(false);
        }
    };

    const deleteBlog = async (id) => {
        if (!window.confirm("Delete this post permanently?")) return;
        try {
            await api.delete(`/admin/blogs/${id}`);
            toast.success("Post deleted");
            loadBlogs();
        } catch (err) {
            toast.error(formatApiError(err));
        }
    };

    const inputCls = "w-full rounded-xl border border-white/10 bg-white/5 px-4 py-2.5 text-sm text-white placeholder:text-slate-500 outline-none focus:border-cyan-500/60";

    return (
        <div className="min-h-screen bg-[#080C14]" data-testid="admin-dashboard">
            <header className="glass sticky top-0 z-40 border-b border-white/10">
                <div className="mx-auto flex h-16 max-w-6xl items-center justify-between px-4 sm:px-6">
                    <Link to="/"><Logo compact /></Link>
                    <div className="flex items-center gap-3">
                        <span className="hidden text-xs text-slate-500 sm:inline notranslate">{user.email}</span>
                        <button
                            onClick={logout}
                            data-testid="admin-logout-button"
                            className="flex items-center gap-1.5 rounded-full border border-white/10 px-3.5 py-1.5 text-xs font-medium text-slate-300 hover:border-red-500/40 hover:text-red-300"
                        >
                            <LogOut className="h-3.5 w-3.5" /> Logout
                        </button>
                    </div>
                </div>
            </header>

            <main className="mx-auto max-w-6xl px-4 py-8 sm:px-6">
                <div className="flex gap-2" data-testid="admin-tabs">
                    {[
                        { id: "blogs", label: "Blog Posts", icon: Newspaper, count: blogs.length },
                        { id: "leads", label: "Leads", icon: Users, count: leads.length },
                    ].map((t) => (
                        <button
                            key={t.id}
                            data-testid={`admin-tab-${t.id}`}
                            onClick={() => setTab(t.id)}
                            className={`flex items-center gap-2 rounded-full px-5 py-2.5 text-sm font-semibold transition-colors ${
                                tab === t.id ? "bg-cyan-500/15 text-cyan-300 border border-cyan-500/40" : "border border-white/10 text-slate-400 hover:text-white"
                            }`}
                        >
                            <t.icon className="h-4 w-4" /> {t.label}
                            <span className="rounded-full bg-white/10 px-2 py-0.5 text-[10px]">{t.count}</span>
                        </button>
                    ))}
                </div>

                {tab === "blogs" && (
                    <div className="mt-8" data-testid="admin-blogs-panel">
                        <button
                            onClick={() => setEditing({ ...EMPTY })}
                            data-testid="admin-new-post-button"
                            className="mb-6 flex items-center gap-2 rounded-full bg-gradient-to-r from-blue-600 to-cyan-500 px-5 py-2.5 text-sm font-semibold text-white"
                        >
                            <Plus className="h-4 w-4" /> New Post
                        </button>

                        {editing && (
                            <form onSubmit={saveBlog} className="glow-card mb-8 space-y-4 rounded-2xl p-6" data-testid="admin-blog-editor">
                                <div className="flex items-center justify-between">
                                    <h2 className="font-display text-lg font-bold text-white">{editing.id ? "Edit Post" : "New Post"}</h2>
                                    <button type="button" onClick={() => setEditing(null)} data-testid="admin-editor-close" className="text-slate-500 hover:text-white">
                                        <X className="h-5 w-5" />
                                    </button>
                                </div>
                                <input required minLength={3} data-testid="editor-title-input" className={inputCls} placeholder="Post title *" value={editing.title} onChange={(e) => setEditing({ ...editing, title: e.target.value })} />
                                <input data-testid="editor-excerpt-input" className={inputCls} placeholder="Short excerpt (shown on cards)" value={editing.excerpt} onChange={(e) => setEditing({ ...editing, excerpt: e.target.value })} />
                                <div className="grid gap-4 sm:grid-cols-3">
                                    <input data-testid="editor-category-input" className={inputCls} placeholder="Category" value={editing.category} onChange={(e) => setEditing({ ...editing, category: e.target.value })} />
                                    <select data-testid="editor-cover-select" className={inputCls} value={editing.cover} onChange={(e) => setEditing({ ...editing, cover: e.target.value })}>
                                        {COVERS.map((c) => (
                                            <option key={c} value={c} className="bg-[#0D1424]">{c} cover</option>
                                        ))}
                                    </select>
                                    <label className="flex items-center gap-2 text-sm text-slate-300">
                                        <input type="checkbox" data-testid="editor-published-checkbox" checked={editing.published} onChange={(e) => setEditing({ ...editing, published: e.target.checked })} className="h-4 w-4 accent-cyan-500" />
                                        Published
                                    </label>
                                </div>
                                <textarea
                                    rows={10}
                                    data-testid="editor-content-textarea"
                                    className={`${inputCls} resize-y font-mono2 text-xs leading-relaxed`}
                                    placeholder={"Write your article…\n\nUse '## ' for headings and '- ' for bullet points."}
                                    value={editing.content}
                                    onChange={(e) => setEditing({ ...editing, content: e.target.value })}
                                />
                                <button type="submit" disabled={saving} data-testid="editor-save-button" className="flex items-center gap-2 rounded-xl bg-gradient-to-r from-blue-600 to-cyan-500 px-6 py-3 text-sm font-semibold text-white disabled:opacity-60">
                                    {saving && <Loader2 className="h-4 w-4 animate-spin" />}
                                    {editing.id ? "Save Changes" : "Publish Post"}
                                </button>
                            </form>
                        )}

                        <div className="space-y-3">
                            {blogs.map((b) => (
                                <div key={b.id} className="glow-card flex items-center justify-between gap-4 rounded-xl p-4" data-testid={`admin-blog-row-${b.slug}`}>
                                    <div className="min-w-0">
                                        <p className="truncate font-display text-sm font-bold text-white">{b.title}</p>
                                        <p className="mt-0.5 text-xs text-slate-500">
                                            {b.category} · {new Date(b.created_at).toLocaleDateString("en-IN")} ·{" "}
                                            <span className={b.published ? "text-emerald-400" : "text-amber-400"}>{b.published ? "Published" : "Draft"}</span>
                                        </p>
                                    </div>
                                    <div className="flex shrink-0 gap-2">
                                        <button onClick={() => setEditing({ ...b })} data-testid={`admin-edit-${b.slug}`} className="rounded-lg border border-white/10 p-2 text-slate-400 hover:border-cyan-500/40 hover:text-cyan-300">
                                            <Pencil className="h-4 w-4" />
                                        </button>
                                        <button onClick={() => deleteBlog(b.id)} data-testid={`admin-delete-${b.slug}`} className="rounded-lg border border-white/10 p-2 text-slate-400 hover:border-red-500/40 hover:text-red-300">
                                            <Trash2 className="h-4 w-4" />
                                        </button>
                                    </div>
                                </div>
                            ))}
                            {blogs.length === 0 && <p className="py-10 text-center text-sm text-slate-500">No posts yet. Write your first one!</p>}
                        </div>
                    </div>
                )}

                {tab === "leads" && (
                    <div className="mt-8" data-testid="admin-leads-panel">
                        <div className="space-y-3">
                            {leads.map((l) => (
                                <div key={l.id} className="glow-card rounded-xl p-5" data-testid={`admin-lead-${l.id}`}>
                                    <div className="flex flex-wrap items-center justify-between gap-2">
                                        <p className="font-display text-sm font-bold text-white">{l.name}</p>
                                        <span className={`rounded-full px-3 py-1 text-[10px] font-bold uppercase tracking-wider ${
                                            l.type === "investor" ? "bg-amber-500/15 text-amber-300" : "bg-cyan-500/15 text-cyan-300"
                                        }`}>
                                            {l.type}
                                        </span>
                                    </div>
                                    <p className="mt-1 text-xs text-slate-500 notranslate">
                                        {l.email} {l.phone && `· ${l.phone}`} {l.company && `· ${l.company}`}
                                    </p>
                                    <p className="mt-2.5 text-sm leading-relaxed text-slate-300">{l.message}</p>
                                    <p className="mt-2 text-[10px] text-slate-600">{new Date(l.created_at).toLocaleString("en-IN")}</p>
                                </div>
                            ))}
                            {leads.length === 0 && <p className="py-10 text-center text-sm text-slate-500">No leads yet.</p>}
                        </div>
                    </div>
                )}
            </main>
        </div>
    );
}
